import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { computeCompliance, GROUP_ORDER, bandStatus } from './rotaCompliance';
import { displayRole } from './roleLabel';
import './RotaComplianceTable.css';

const pct = (violating, total) =>
  total === 0 ? null : `${Math.round((violating / total) * 100)}%`;

const fmtCount = (cell, format) => {
  if (format === 'hours') return `(${cell.violating.toFixed(1)}h/${cell.total.toFixed(1)}h)`;
  // 'index' metrics (e.g. rotation Gini) show the % as the value; the count of graded
  // shifts is the useful denominator, not the derived gini·n numerator.
  if (format === 'index') {
    const n = Math.round(cell.total);
    return `(${n} shift${n === 1 ? '' : 's'})`;
  }
  return `(${cell.violating}/${cell.total})`;
};

// Colour comes from the check's configured green/amber/red band (see COLOUR_BANDS).
const Cell = ({ cell, band, format }) => {
  if (!cell || cell.total === 0) return <span className="rc-cell-na">—</span>;
  return (
    <span>
      {pct(cell.violating, cell.total)}
      <span className="rc-cell-count"> {fmtCount(cell, format)}</span>
    </span>
  );
};

const AREA_SHORT = { FRONT_OF_HOUSE: 'FOH', BACK_OF_HOUSE: 'BOH', BAR: 'Bar' };
const formatGap = (g) => {
  const day = g.day || '';
  const window = g.window || '';
  const source = g.source === 'lp' ? 'LP gate' : g.source === 'tier2' ? 'Tier 2' : g.source || '';
  const shape = g.shape_of_day_type && g.shape_of_day_type !== 'key_holder_gap' ? ` · ${g.shape_of_day_type}` : '';
  const area = g.area ? (AREA_SHORT[g.area] || g.area) : '';
  return { day, window, source, shape, area };
};

const RotaComplianceTable = ({
  tableRows,
  memberSchedule,
  config,
  partnerId,
  shapeOfDay,
  slotDiffMap,
  breaks,
  bandDeltas,
  setShiftOutcomes,
  idealMatchEnabled,
  setIdealMatchEnabled,
  idealMatchBuffer,
  setIdealMatchBuffer,
  boundaryMatchEnabled,
  setBoundaryMatchEnabled,
  boundaryMatchBuffer,
  setBoundaryMatchBuffer,
  unfilledKeyHolderShifts = [],
  keyHolderRescues = [],
  unfilledSectionShifts = [],
  sectionKeyHolderRescues = [],
  contractedShortfall = [],
}) => {
  // Only workers who actually missed their target (the backend lists every contracted
  // worker so the dashboard can render target-vs-scheduled; the alert shows shortfalls).
  const shortedContracted = contractedShortfall.filter((w) => w && w.cause);
  const [isExpanded, setIsExpanded] = useState(false);
  const [openCheck, setOpenCheck] = useState(null);
  const [deprivedThresholdPct, setDeprivedThresholdPct] = useState(50);

  const { checks, roles } = useMemo(
    () => computeCompliance({
      tableRows, memberSchedule, config, shapeOfDay, slotDiffMap, breaks, bandDeltas, setShiftOutcomes, deprivedThresholdPct,
      idealMatchEnabled, idealMatchBuffer, boundaryMatchEnabled, boundaryMatchBuffer,
    }),
    [tableRows, memberSchedule, config, shapeOfDay, slotDiffMap, breaks, bandDeltas, setShiftOutcomes, deprivedThresholdPct,
      idealMatchEnabled, idealMatchBuffer, boundaryMatchEnabled, boundaryMatchBuffer],
  );

  const hardViolations = useMemo(
    () => checks.filter((c) => c.severity === 'hard' && c.total.violating > 0),
    [checks],
  );

  const colCount = roles.length + 2; // Check + total + roles

  const renderRow = (check) => {
    const isOpen = openCheck === check.key;
    const hasOffenders = check.total.offenders.length > 0;
    return (
      <React.Fragment key={check.key}>
        <tr
          className={`rc-row${hasOffenders ? ' rc-row--clickable' : ''}`}
          onClick={() => hasOffenders && setOpenCheck(isOpen ? null : check.key)}
        >
          <td className="rc-check">
            {check.label}
            {check.severity !== 'info' && (
              <span className={`rc-sev rc-sev--${check.severity}`}>{check.severity}</span>
            )}
          </td>
          <td className={`rc-total-col rc-cell--${bandStatus(check.total, check.band)}`}>
            <Cell cell={check.total} band={check.band} format={check.format} />
          </td>
          {roles.map((role) => (
            <td key={role} className={`rc-cell--${bandStatus(check.byRole[role], check.band)}`}>
              <Cell cell={check.byRole[role]} band={check.band} format={check.format} />
            </td>
          ))}
        </tr>
        {isOpen && hasOffenders && (
          <tr className="rc-offenders-row">
            <td colSpan={colCount}>
              <ul className="rc-offenders">
                {check.total.offenders.map((o, i) => (
                  <li key={`${check.key}-${o.name}-${i}`}>
                    <span className="rc-offender-name">{o.name}</span>
                    <span className="rc-offender-role">{o.role ? displayRole(o.role, partnerId) : ''}</span>
                    <span className="rc-offender-detail">{o.detail}</span>
                  </li>
                ))}
              </ul>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="collapsible-section rota-compliance" style={{ margin: '8px 0' }}>
      <div className="collapsible-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h2 className="collapsible-title">
          Rota analysis &amp; compliance
          {hardViolations.length > 0 ? (
            <span className="rc-headline rc-headline--bad">
              {hardViolations.length} hard violation{hardViolations.length === 1 ? '' : 's'}
              <span className="rc-headline__detail">
                {' · '}{hardViolations.map((c) => c.label).join(', ')}
              </span>
            </span>
          ) : (
            <span className="rc-headline rc-headline--ok">all hard checks clear</span>
          )}
          {unfilledKeyHolderShifts.length > 0 && (
            <span className="rc-headline rc-headline--bad" style={{ marginLeft: 12 }}>
              {unfilledKeyHolderShifts.length} unfilled key-holder shift{unfilledKeyHolderShifts.length === 1 ? '' : 's'}
            </span>
          )}
          {unfilledSectionShifts.length > 0 && (
            <span className="rc-headline rc-headline--bad" style={{ marginLeft: 12 }}>
              {unfilledSectionShifts.length} unfilled section shift{unfilledSectionShifts.length === 1 ? '' : 's'}
            </span>
          )}
          {shortedContracted.length > 0 && (
            <span className="rc-headline rc-headline--bad" style={{ marginLeft: 12 }}>
              {shortedContracted.length} contracted-hours shortfall{shortedContracted.length === 1 ? '' : 's'}
            </span>
          )}
        </h2>
        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>
      {isExpanded && (
        <div className="rc-body">
          {unfilledKeyHolderShifts.length > 0 && (
            <div className="rc-alert rc-alert--bad">
              <div className="rc-alert__title">Unfilled Key-Holder Shifts</div>
              <ul className="rc-alert__list">
                {unfilledKeyHolderShifts.map((g, i) => {
                  const f = formatGap(g);
                  return (
                    <li key={g.shift_id || `${f.day}-${f.window}-${f.source}-${i}`}>
                      <strong>{f.day}</strong> {f.window}{f.shape}
                      <span className="rc-alert__src"> · {f.source}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {unfilledSectionShifts.length > 0 && (
            <div className="rc-alert rc-alert--bad">
              <div className="rc-alert__title">
                Unfilled Section Shifts
                <span className="rc-alert__note"> (area requires an opener/closer but none available)</span>
              </div>
              <ul className="rc-alert__list">
                {unfilledSectionShifts.map((g, i) => {
                  const f = formatGap(g);
                  return (
                    <li key={g.shift_id || `${f.area}-${f.day}-${f.window}-${i}`}>
                      {f.area && <strong>{f.area}</strong>} <strong>{f.day}</strong> {f.window}{f.shape}
                      <span className="rc-alert__src"> · {f.source}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {shortedContracted.length > 0 && (
            <div className="rc-alert rc-alert--bad">
              <div className="rc-alert__title">
                Contracted-Hours Shortfalls
                <span className="rc-alert__note"> (EXACT/MINIMUM worker missed their target)</span>
              </div>
              <ul className="rc-alert__list">
                {shortedContracted.map((w, i) => (
                  <li key={w.emp_id || `${w.name}-${i}`}>
                    <strong>{w.name}</strong>
                    <span className="rc-alert__src"> · {w.constraint_mode}</span>
                    {' · '}{w.detail || `${w.scheduled}h vs ${w.target}h`}
                    <span className="rc-alert__src"> · {String(w.cause).replace(/_/g, ' ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(keyHolderRescues.length > 0 || sectionKeyHolderRescues.length > 0) && (
            <div className="rc-alert rc-alert--muted">
              <div className="rc-alert__title rc-alert__title--muted">
                Key-Holder / Section Rescues
                <span className="rc-alert__note"> (LP flagged a gap; Tier 2 covered it)</span>
              </div>
              <ul className="rc-alert__list rc-alert__list--muted">
                {[...keyHolderRescues, ...sectionKeyHolderRescues].map((g, i) => {
                  const f = formatGap(g);
                  return (
                    <li key={`${f.area}-${f.day}-${f.window}-rescue-${i}`}>
                      {f.area && <strong>{f.area}</strong>} <strong>{f.day}</strong> {f.window}{f.shape}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="rc-controls">
            <span className="rc-controls__label">Indicators</span>
            <div className={`rc-ctrl-group${idealMatchEnabled ? ' is-on' : ''}`}>
              <label className="rc-toggle">
                <input
                  type="checkbox"
                  checked={idealMatchEnabled}
                  onChange={(e) => setIdealMatchEnabled(e.target.checked)}
                />
                <span>Ideal-window</span>
              </label>
              <label className="rc-buffer">
                <span>± buffer</span>
                <input
                  type="number" min="0" step="0.25" value={idealMatchBuffer} disabled={!idealMatchEnabled}
                  onChange={(e) => { const v = parseFloat(e.target.value); setIdealMatchBuffer(Number.isFinite(v) && v >= 0 ? v : 0); }}
                />
                <span>h</span>
              </label>
            </div>
            <div className={`rc-ctrl-group${boundaryMatchEnabled ? ' is-on' : ''}`}>
              <label className="rc-toggle">
                <input
                  type="checkbox"
                  checked={boundaryMatchEnabled}
                  onChange={(e) => setBoundaryMatchEnabled(e.target.checked)}
                />
                <span>Start/end</span>
              </label>
              <label className="rc-buffer">
                <span>± buffer</span>
                <input
                  type="number" min="0" step="0.25" value={boundaryMatchBuffer} disabled={!boundaryMatchEnabled}
                  onChange={(e) => { const v = parseFloat(e.target.value); setBoundaryMatchBuffer(Number.isFinite(v) && v >= 0 ? v : 0); }}
                />
                <span>h</span>
              </label>
            </div>
            <label className="rc-threshold">
              <span>Heavily-deprived ≥</span>
              <input
                type="number" min="0" max="100" step="5" value={deprivedThresholdPct}
                onChange={(e) => { const v = parseInt(e.target.value, 10); setDeprivedThresholdPct(Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 50); }}
              />
              <span>% short</span>
            </label>
          </div>

          <table className="rc-table">
            <thead>
              <tr>
                <th className="rc-check">Check</th>
                <th className="rc-total-col">All roles</th>
                {roles.map((role) => (
                  <th key={role}>{displayRole(role, partnerId)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GROUP_ORDER.map((group) => {
                // Drop checks that apply to nobody (denominator 0 — e.g. no Max_Weekly_Hours set),
                // unless flagged alwaysShow (renders as grey "—").
                const groupChecks = checks.filter((c) => c.group === group && (c.total.total > 0 || c.alwaysShow));
                if (groupChecks.length === 0) return null;
                return (
                  <React.Fragment key={group}>
                    <tr className="rc-group-row">
                      <td colSpan={colCount}>{group}</td>
                    </tr>
                    {groupChecks.map(renderRow)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="rc-hint">
            Hard/soft rows flag breaches (click to list who/what is affected). Rota-analysis rows are
            informational metrics — match rates and hours-weighted stretch/deprivation.
          </div>
        </div>
      )}
    </div>
  );
};

export default RotaComplianceTable;
